// src/lib/utils/clientMetadata.ts
//
// Single source of truth for the marketing / attribution metadata the browser
// clients collect and POST as payload._meta.
//
// Imported by the React embed (src/scripts/kiroku-embed.ts, bundled by esbuild)
// and the hosted form (src/components/forms/LiveForm.tsx, compiled by Vite).
// The HTML embed (src/scripts/kiroku-form.ts) is tsc-compiled with
// module:"None" and cannot import this module, so it keeps an inline copy of
// the same capture; tests/guard/client-meta-parity.test.ts holds that copy in
// sync against MARKETING_PARAMS below.
//
// Pure module: no Node-only imports, so it bundles cleanly into the standalone
// client scripts.

/** Query-string params that carry marketing attribution, captured verbatim. */
export const MARKETING_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid", // Google Ads click ID
  "fbclid", // Meta click ID
  "msclkid", // Microsoft / Bing click ID
] as const;

export type MarketingParam = (typeof MARKETING_PARAMS)[number];

export interface ClientMeta {
  source: string;
  version: string;
  timestamp: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  intentTier?: string;
  concernsOpened?: string[];
}

export interface BuildClientMetaOptions {
  /** Raw query string, e.g. `window.location.search`. */
  search: string;
  /** Page referrer (`document.referrer`) if available. */
  referrer?: string;
  /** Identifies which client built the payload (e.g. "kiroku-embed-react"). */
  source: string;
  /** Client script version. */
  version: string;
  /** Whether the form has marketing capture (`captureMetadata`) enabled. */
  captureMarketing: boolean;
  /**
   * The latest VeritaMetrics intent tier ("cold" | "warm" | "hot") this
   * session reached, when the host page runs VeritaMetrics and tracking
   * consent allows it. Plan 3.5 of docs/guided-shopping_plan.md: the caller
   * decides consent and passes `undefined` when it is denied, so this module
   * stays a pure formatter with no consent logic of its own.
   */
  intentTier?: string;
  /**
   * Plan 5.5 of docs/guided-shopping_plan.md: labels of guided-shopping
   * concerns ("Questions?" buttons) the visitor opened before submitting.
   * Needs no consent to record -- unlike the `guided_concern_opened`
   * analytics event sent at open time, the visitor chose to submit this
   * (CLAUDE.md §13). Omitted when empty, matching every other field here.
   */
  concernsOpened?: string[];
}

/**
 * Builds the `_meta` object posted with a submission. Base fields (source,
 * version, timestamp, referrer) are always present. UTM and click-ID params
 * are read from `search` only when `captureMarketing` is true. Absent params
 * are omitted rather than set to undefined, so they do not survive
 * JSON.stringify as null keys.
 */
export function buildClientMeta(options: BuildClientMetaOptions): ClientMeta {
  const {
    search,
    referrer,
    source,
    version,
    captureMarketing,
    intentTier,
    concernsOpened,
  } = options;

  const meta: ClientMeta = {
    source,
    version,
    timestamp: new Date().toISOString(),
  };
  if (referrer) meta.referrer = referrer;
  if (intentTier) meta.intentTier = intentTier;
  if (concernsOpened && concernsOpened.length > 0) {
    meta.concernsOpened = concernsOpened;
  }

  if (captureMarketing) {
    const params = new URLSearchParams(search);
    for (const key of MARKETING_PARAMS) {
      const value = params.get(key);
      if (value) meta[key] = value;
    }
  }

  return meta;
}
